import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

export interface StatementTransaction {
  transactionId: string;
  date: string;
  value: number;
  description: string;
}

export interface StartReconciliationInput {
  accountId: string;
  openingBalance: number;
  closingBalance: number;
  statementDate: string;
  periodStart: string;
  periodEnd: string;
  importedTransactions: StatementTransaction[];
}

/** Confianca do casamento, como o motor do dominio a classifica. */
export type MatchConfidence = 'HIGH' | 'MEDIUM' | 'LOW';

export interface ReconciliationTransaction {
  id: string;
  description: string;
  amount: number;
  date: string;
  isImported: boolean;
  /* O casamento vem do servidor: o motor compara valor, data, documento e
     descricao e classifica a confianca. Refazer isso na tela daria outro
     resultado — e a tela nao ve os lancamentos do ERP que nao casaram. */
  matchedTransactionId?: string | null;
  matchConfidence?: MatchConfidence | null;
  matchDifference?: number | null;
  matchedDescription?: string | null;
  matchedAmount?: number | null;
  matchedDate?: string | null;
  unmatchedReason?: string | null;
}

export interface ReconciliationUnmatchedEntry {
  id: string;
  transactionId: string;
  /** IMPORTED_ONLY = so no extrato; SYSTEM_ONLY = so no ERP. */
  type: 'IMPORTED_ONLY' | 'SYSTEM_ONLY';
  reason: string;
  description?: string | null;
  amount?: number | null;
  date?: string | null;
}

export interface Reconciliation {
  id: string;
  accountId: string;
  accountName: string;
  statementDate: string;
  periodStart: string;
  periodEnd: string;
  openingBalance: number;
  closingBalance: number;
  systemBalance: number;
  difference: number;
  status: 'DRAFT' | 'CONFIRMED';
  notes?: string | null;
  transactions: ReconciliationTransaction[];
  unmatchedEntries: ReconciliationUnmatchedEntry[];
  /** Linhas do extrato com par encontrado, e total importado. */
  matchedCount: number;
  importedCount: number;
  createdAt: string;
  updatedAt: string;
}

/* ── Hooks ── */

export function useReconciliation(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.finance.reconciliation(), id],
    queryFn: () => apiClient.get<Reconciliation>(`/finance/reconciliation/${id}`),
    enabled: !!id,
  });
}

export function useReconciliations() {
  return useQuery({
    queryKey: queryKeys.finance.reconciliation(),
    queryFn: () =>
      apiClient.get<{ data: Reconciliation[]; total: number; page: number; pageSize: number; totalPages: number }>(
        '/finance/reconciliation',
      ),
  });
}

export function useStartReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartReconciliationInput) =>
      apiClient.post<Reconciliation>('/finance/reconciliation', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.reconciliation() });
    },
  });
}

export function useConfirmReconciliation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<Reconciliation>(`/finance/reconciliation/${id}/confirm`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.finance.reconciliation() });
      qc.invalidateQueries({ queryKey: [...queryKeys.finance.reconciliation(), id] });
    },
  });
}
